import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  IconButton,
  InputAdornment,
  Link,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  clearSourceChannelDetail,
  createSourceChannel,
  fetchSourceChannelById,
  generateSourceChannelSecret,
  updateSourceChannel
} from '../store/slices/sourceChannelsSlice';

const statusOptions = [
  { label: 'ACTIVE', value: 'ACTIVE' },
  { label: 'INACTIVE', value: 'INACTIVE' }
];

const SourceChannelFormPage = ({ mode = 'create' }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const rawId = params.id ? Number(params.id) : null;
  const editingId = Number.isFinite(rawId) ? rawId : null;
  const isEditMode = mode === 'edit' || Boolean(editingId);

  const { detail, detailLoading, detailError, saveStatus, saveError, secretStatus } = useSelector(
    (state) => state.sourceChannels
  );

  const [formValues, setFormValues] = useState({
    name: '',
    status: 'ACTIVE',
    secretKey: ''
  });
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (isEditMode && editingId) {
      dispatch(fetchSourceChannelById(editingId));
    } else {
      setFormValues({ name: '', status: 'ACTIVE', secretKey: '' });
    }
    return () => {
      dispatch(clearSourceChannelDetail());
    };
  }, [dispatch, isEditMode, editingId]);

  useEffect(() => {
    if (detail && isEditMode && detail.id === editingId) {
      setFormValues({
        name: detail.name || '',
        status: detail.status || 'ACTIVE',
        secretKey: detail.secret_key || detail.secretKey || ''
      });
    }
  }, [detail, isEditMode, editingId]);

  const handleChange = (field) => (event) => {
    setFormValues((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const validate = () => {
    if (!formValues.name.trim()) return 'Name is required.';
    if (formValues.name.trim().length > 50) return 'Name must be 50 characters or less.';
    if (!formValues.status) return 'Status is required.';
    if (!formValues.secretKey) return 'Secret key is required.';
    if (String(formValues.secretKey).length > 50) return 'Secret key must be 50 characters or less.';
    return '';
  };

  const handleGenerateSecret = async () => {
    setFormError('');
    const result = await dispatch(generateSourceChannelSecret()).unwrap();
    if (result) {
      setFormValues((prev) => ({ ...prev, secretKey: result }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validation = validate();
    if (validation) {
      setFormError(validation);
      return;
    }
    setFormError('');
    const payload = {
      name: formValues.name.trim(),
      status: formValues.status,
      secretKey: formValues.secretKey
    };
    try {
      if (isEditMode && editingId) {
        await dispatch(updateSourceChannel({ id: editingId, ...payload })).unwrap();
      } else {
        await dispatch(createSourceChannel(payload)).unwrap();
      }
      navigate('/source-channels', { replace: true });
    } catch (err) {
      setFormError(err || 'Failed to save source channel.');
    }
  };

  return (
    <Stack spacing={3}>
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2
        }}
      >
        <Box>
          <Breadcrumbs
            aria-label="breadcrumb"
            separator="/"
            sx={{ fontSize: 12, mb: 1, color: 'text.secondary' }}
          >
            <Link underline="hover" color="inherit" href="#">
              Dashboard
            </Link>
            <Link underline="hover" color="inherit" href="#">
              Management
            </Link>
            <Link underline="hover" color="inherit" href="#" onClick={() => navigate('/source-channels')}>
              Source Channel
            </Link>
            <Typography color="text.primary">{isEditMode ? 'Edit' : 'New'}</Typography>
          </Breadcrumbs>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {isEditMode ? `Edit Source Channel #${editingId}` : 'New Source Channel'}
          </Typography>
          <Typography color="text.secondary">
            {isEditMode ? 'Update source channel details.' : 'Create a new source channel.'}
          </Typography>
        </Box>
      </Box>

      <Paper sx={{ p: { xs: 2, sm: 3 }, boxShadow: '0px 24px 48px rgba(15, 23, 42, 0.05)' }}>
        <Stack spacing={2}>
          {detailError && <Alert severity="error">{detailError}</Alert>}
          {formError && <Alert severity="error">{formError}</Alert>}
          {saveError && <Alert severity="error">{saveError}</Alert>}
          {detailLoading && <Typography color="text.secondary">Loading...</Typography>}

          <TextField
            label="Name"
            value={formValues.name}
            onChange={handleChange('name')}
            inputProps={{ maxLength: 50 }}
            required
            fullWidth
          />

          <TextField
            select
            label="Status"
            value={formValues.status}
            onChange={handleChange('status')}
            required
            fullWidth
          >
            {statusOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Secret Key"
            value={formValues.secretKey}
            inputProps={{ maxLength: 50, readOnly: true }}
            required
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleGenerateSecret}
                    disabled={secretStatus === 'loading'}
                    edge="end"
                  >
                    <AutorenewIcon />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end">
            <Button variant="outlined" color="inherit" onClick={() => navigate('/source-channels')}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={saveStatus === 'loading'}
            >
              Submit
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  );
};

export default SourceChannelFormPage;
